import { useEffect, useState } from 'react';
import { Alert, Anchor, Button, Center, Loader, PasswordInput, Text } from '@mantine/core';
import { Link, useSearchParams } from 'react-router-dom';

import { MIN_PASSWORD, checkPasswordLink, setPassword } from '../lib/auth';
import { AuthCard } from './SignInPage';

/**
 * Where the link in a "new password" mail lands.
 *
 * The link is checked before the field is shown, and checking does not spend
 * it: an expired link should say so before somebody chooses a password, not
 * after. Setting one ends every session the account had, which is the point
 * of the flow when the reason for it was that somebody else knows the old
 * password.
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setValue] = useState('');
  const [state, setState] = useState<'checking' | 'ready' | 'done'>('checking');
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(new Error('This link is incomplete.'));
      return;
    }
    checkPasswordLink(token)
      .then(() => setState('ready'))
      .catch(setError);
  }, [token]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await setPassword(token, password);
      setState('done');
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title="Choose a password">
      {error ? (
        <Alert color="red" variant="light">
          {error.message}
        </Alert>
      ) : null}

      {state === 'done' ? (
        <>
          <Text size="sm">The password is set. Every other session of this account has ended.</Text>
          <Anchor component={Link} to="/" size="sm">
            Sign in now
          </Anchor>
        </>
      ) : state === 'ready' ? (
        <>
          <PasswordInput
            label="New password"
            description={`At least ${MIN_PASSWORD} characters.`}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password.length >= MIN_PASSWORD) void submit();
            }}
          />
          <Button loading={busy} disabled={password.length < MIN_PASSWORD} onClick={() => void submit()}>
            Save the password
          </Button>
        </>
      ) : error ? (
        <Anchor component={Link} to="/" size="sm">
          To sign-in
        </Anchor>
      ) : (
        <Center py="md">
          <Loader />
        </Center>
      )}
    </AuthCard>
  );
}
